/**
 * Pure helpers for the device "Location & photos" section (installer note + up to 3 photos).
 *
 * No DOM, no React: everything here runs under `node --test` (see devicePhotos.test.ts). The
 * browser-only image preparation lives in devicePhotoPrep.ts.
 *
 * The limits MIRROR the controller (services/device_photos.py, models/device_photo.py). The
 * server is the authority — these exist so an installer on a tablet gets the answer before a
 * slow upload over site LTE, not instead of the server's check.
 */

/** Photos per device (DB-enforced slot limit). */
export const MAX_PHOTOS = 3;
/** Installer note length (services/device_photos.DESCRIPTION_MAX_LEN). */
export const DESCRIPTION_MAX = 1000;
/** Caption length (models/device_photo.CAPTION_MAX_LEN). */
export const CAPTION_MAX = 140;
/** Upload size ceiling per photo (services/device_photos.MAX_UPLOAD_BYTES). */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/** Longest edge the browser downscales to before uploading (same box the server stores). */
export const CLIENT_MAX_EDGE = 2560;

/** "2 of 3" — how many of the device's photo slots are used. */
export function slotCountLabel(count: number, max: number = MAX_PHOTOS): string {
  const used = Math.max(0, Math.min(Math.floor(count), max));
  return `${used} of ${max}`;
}

/** Scale (width, height) to fit inside a maxEdge box, preserving aspect. Never upscales. */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number = CLIENT_MAX_EDGE,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * One-line preview of an installer note for the device list: whitespace (incl. line breaks)
 * collapsed, cut at a word boundary where possible, "…" appended when shortened.
 * null for an empty/absent note so callers can skip the line entirely.
 */
export function descriptionPreview(
  text: string | null | undefined,
  maxChars = 90,
): string | null {
  const flat = (text ?? "").replace(/\s+/g, " ").trim();
  if (!flat) return null;
  if (flat.length <= maxChars) return flat;
  const cut = flat.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(" ");
  // Prefer a word boundary, but not if it would throw away most of the preview.
  const base = lastSpace > maxChars * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${base.trimEnd()}…`;
}

/** Characters left before a limit (negative when over). */
export function charsLeft(text: string, max: number): number {
  return max - text.length;
}

/**
 * Client-side pre-check of a picked file. Returns a plain error sentence, or null when the
 * file may proceed. Size is NOT checked here: a large camera photo is downscaled in the
 * browser first, so only the prepared blob's size matters (see uploadTooLarge).
 *
 * Some Android pickers report an empty MIME type for HEIC, so the extension is a fallback.
 */
export function precheckPhotoFile(file: { type: string; name: string; size: number }): string | null {
  if (file.size === 0) return "That file is empty.";
  const type = (file.type || "").toLowerCase();
  if (type.startsWith("image/")) return null;
  if (!type && /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) return null;
  return "Only photos can be added (JPEG, PNG, WebP or HEIC).";
}

/** True when a prepared upload exceeds the server's per-photo limit. */
export function uploadTooLarge(bytes: number): boolean {
  return bytes > MAX_UPLOAD_BYTES;
}

/** Whole-percent upload progress, clamped to 0..100 (0 when the total is unknown). */
export function progressPercent(loaded: number, total: number): number {
  if (!total || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
}

/** File name for a browser-re-encoded JPEG: same stem, .jpg extension. */
export function reencodedName(name: string): string {
  const stem = name.replace(/\.[^./\\]+$/, "") || "photo";
  return `${stem}.jpg`;
}

/** Human file size: "820 KB", "1.4 MB". */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The plain sentence shown for a failed photo request.
 *
 * `serverMessage` is the controller's {"error"} text when it sent one. For 400 and 503 that
 * text IS the explanation ("This device already has 3 photos…"), so it is used verbatim. A
 * fallback "413 Request Entity Too Large"-style message (no JSON body — e.g. a proxy answered)
 * is never shown raw.
 */
export function uploadErrorMessage(status: number, serverMessage?: string | null): string {
  const usable = serverMessage && !/^\d{3}\b/.test(serverMessage.trim()) ? serverMessage.trim() : null;
  switch (status) {
    case 0:
      return "No connection to the controller. Check the network and try again.";
    case 400:
      return usable ?? "The photo was rejected.";
    case 401:
      return "Your session has expired. Sign in again.";
    case 403:
      return "You don't have permission to change photos on this device.";
    case 404:
      return "This device or photo is no longer available.";
    case 413:
      return "Photo is larger than the upload limit (10 MB).";
    case 503:
      return usable ?? "Photo storage is not available right now.";
    default:
      return usable ?? `Something went wrong (${status}). Try again.`;
  }
}
